import { Button, Checkbox, FileInput, Group, TextInput, Select, NumberInput, Stack, Text, Divider, Image, Flex, ActionIcon, Box, Tooltip, Textarea, Modal, Paper, Table, Anchor, SimpleGrid } from '@mantine/core'
import { IconPhoto, IconFiles, IconVideo, IconChevronDown, IconX, IconExclamationCircle, IconDownload, IconEye, IconExternalLink, IconHelpCircle } from '@tabler/icons-react';
import { renderFile, renderVideo } from '../../../utils/renderFile'
import { useState } from 'react';
import { isBase64 } from '../../../utils/isBase64';
import CommunityStatCard from './CommunityStatsCard';


const COMPRESS_TOOLS = [
   {
      label: 'Compress PNG',
      value: "https://compresspng.com",
      utilty: 'JPG, PNG, GIF',
   },
   {
      label: 'iLoveZip',
      value: "https://www.ilovezip.com/compress-zip-file",
      utilty: 'ZIP',
   },
   {
      label: 'iLovePDF',
      value: "https://www.ilovepdf.com/compress_pdf",
      utilty: 'PDF',
   },
   {
      label: 'FreeConvert',
      value: "https://www.freeconvert.com/video-compressor",
      utilty: 'Video',
   },
   {
      value: "https://www.online-convert.com/es/compresor-de-video/comprimir-mp4",
      label: 'Online-Convert',
      utilty: 'MP4',
   },



]

export default function CommunityResourceForm({
   form,
   isLoading,
}) {

   // Función para procesar múltiples archivos (Masonry)
   const handleMultipleFiles = async (files) => {
      const base64Files = await Promise.all(files.map(file => renderFile(file)));
      // Combinamos con las que ya existan o reemplazamos según prefieras
      form.setFieldValue("images", base64Files);
   };

   const handleRemoveImage = (indexToRemove) => {
      const newImages = form.values.images.filter((_, index) => index !== indexToRemove);
      form.setFieldValue("images", newImages);
   };

   const handleTutorialFile = async (file) => {
      if (!file) return;

      if (file.type.startsWith('video/')) {
         const base64Video = await renderVideo(file);
         form.setFieldValue("tutorial_url", base64Video);
      } else if (file.type.startsWith('image/')) {
         const base64Image = await renderFile(file);
         form.setFieldValue("tutorial_url", base64Image);
      }
   };

   const handleDownloadFile = async (file) => {
      if (!file) return;

      const base64 = await renderFile(file, [
         'image/jpeg', 'image/png', 'video/mp4',
         'application/pdf', 'application/zip', 'application/x-zip-compressed'
      ]);
      form.setFieldValue("download_url", base64);
   };

   const isVideoContent = (data) => {
      if (!data) return false;
      return data.includes('video/mp4') || data.includes('video/webm') || data.endsWith('.mp4');
   };

   const [isCompressToolsModal, setIsCompressToolsModal] = useState(false)

   return (
      <Stack className='MktgNewsCreatorForm-wrapper' gap="md">

         {form.values.stats &&
            <>
               <Divider label='Estadísticas' />

               <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
                  <CommunityStatCard icon={<IconEye size={20} />} label="Vistas" value={form.values.stats?.views} color="blue" />
                  <CommunityStatCard icon={<IconDownload size={20} />} label="Descargas" value={form.values.stats?.downloads} color="pink" />
                  <CommunityStatCard icon={<IconExternalLink size={20} />} label="Clicks externos" value={form.values.stats?.external_clicks} color="teal" />
                  <CommunityStatCard icon={<IconHelpCircle size={20} />} label="Clicks tutorial" value={form.values.stats?.tutorial_clicks} color="orange" />
               </SimpleGrid>
            </>
         }

         <Divider label='Estado' />

         <Checkbox
            size='md'
            {...form.getInputProps("is_active", { type: 'checkbox' })}
            label={<span style={{ fontSize: "var(--fs-small)" }}>
               {form?.values?.is_active ? "Está activo y visible para los usuarios" : "No está activo por lo tanto quedará oculto"}
            </span>}
            disabled={isLoading}
         />

         <Divider label='Contenido' />

         <Group grow>
            <TextInput
               {...form.getInputProps("title")}
               label='Título del Recurso'
               placeholder="Ej: Pack Historias WhatsApp"
               disabled={isLoading}
               required
            />

            <Tooltip label="La información se ordena de menor a mayor. Los números mas bajos se mostraràn primeros">
               <NumberInput
                  {...form.getInputProps("display_order")}
                  label='Orden de visualización'
                  disabled={isLoading}
               />
            </Tooltip>

         </Group>

         <Textarea
            {...form.getInputProps("description")}
            label='Descripción'
            placeholder="Explicá brevemente qué es este recurso"
            disabled={isLoading}
         />

         <Group grow>
            <Select
               {...form.getInputProps("category")}
               label="Categoría"
               data={['Kit Digital', 'Tutorial', 'Plantillas', 'Papers', 'Otros']}
               disabled={isLoading}
               required
            />
            <Select
               {...form.getInputProps("type")}
               label="Tipo de contenido"
               data={['video', 'imagen', 'documento', 'enlace', 'zip']}
               disabled={isLoading}
               required
            />
         </Group>

         <TextInput
            {...form.getInputProps("tags")}
            label='Tags (separadas por coma)'
            placeholder="redes, whatsapp, tutorial"
            disabled={isLoading}
         />



         <Divider label="Multimedia" labelPosition="center" />

         <Stack mb={20} gap={0}>
            <Stack align='start' gap={6}
               direction='column'
               maw='max-content'
               fz='var(--fs-small)'
               style={{
                  borderRadius: 'var(--border-radius-sm)'
               }}
            >
               <Flex align="center" gap={6}>
                  <IconExclamationCircle size={24} style={{ flexShrink: 0 }} />
                  <strong>A tener en cuenta</strong>
               </Flex>

               <span>
                  - Evitar archivos muy pesados.
                  <br />
                  - Para un mejor rendimiento de la plataforma, primero <strong>comprimir</strong> los archivos de multimedia para <strong>reducir su tamaño</strong> y luego subirlos.
                  <br />
                  - Revisar que la <strong>calidad</strong> no se pierda luego de la compresión.
               </span>
            </Stack>
            <Button
               w='max-content'
               variant='transparent'
               size='xs'
               m={0}
               p={0}
               color='var(--color-text-primary)'
               style={{ textDecoration: 'underline' }}
               type='button'
               onClick={() => setIsCompressToolsModal(true)}
            >
               Ver herramientas de compresión
            </Button>


            {isCompressToolsModal && (
               <Modal
                  opened={isCompressToolsModal}
                  onClose={() => setIsCompressToolsModal(false)}
                  size="lg"
               >

                  <Stack>
                     <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                           <Table.Tr>
                              <Table.Th>Herramienta</Table.Th>
                              <Table.Th>Utilidad</Table.Th>
                              <Table.Th>Enlace</Table.Th>
                           </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                           {COMPRESS_TOOLS.map((item) => (
                              <Table.Tr key={item.value}>
                                 <Table.Td style={{ fontWeight: 500 }}>{item.label}</Table.Td>
                                 <Table.Td>{item.utilty}</Table.Td>
                                 <Table.Td>
                                    <Anchor href={item.value} target="_blank" size="sm">
                                       Ir al sitio
                                    </Anchor>
                                 </Table.Td>
                              </Table.Tr>
                           ))}
                        </Table.Tbody>
                     </Table>

                     <p style={{ fontSize: '.8rem' }}>
                        *Estas son sugerencias.
                        <br />
                        *Existen muchas más que hacen el mismo trabajo.
                        <br />
                        *Revisar que la calidad no se pierda luego de la compresión.
                     </p>

                  </Stack>
                  <Button mt={30} type='button' fullWidth onClick={() => setIsCompressToolsModal(false)}>Cerrar</Button>
               </Modal>
            )}
         </Stack>

         <Stack grow align="start">
            <Stack w="100%">
               <FileInput
                  label="Imagen de previa (Portada)"
                  placeholder='Cargar miniatura'
                  leftSection={<IconPhoto size={20} />}
                  disabled={isLoading}
                  accept='image/*'
                  required
                  onChange={(file) => {
                     if (file) {
                        renderFile(file).then((_fileBase64) => {
                           form.setFieldValue("preview_url", _fileBase64);
                        });
                     }
                  }}
               />

               {form.values?.preview_url ? (
                  <Box pos="relative" w="max-content">
                     <Image
                        h={120}
                        radius="md"
                        fallbackSrc="https://placehold.co/600x400?text=CARGANDO"
                        src={form?.values?.preview_url}
                     />
                     <ActionIcon
                        variant="filled"
                        color="red"
                        radius="xl"
                        size="xs"
                        pos="absolute"
                        top={-5}
                        right={-5}
                        onClick={() => form.setFieldValue("preview_url", "")}
                     >
                        <IconX size={12} />
                     </ActionIcon>
                  </Box>
               ) :
                  <Flex gap={8} align='center'>
                     <IconX size={16} />
                     <Text size="xs" c="dimmed">
                        Sin imagen previa o de portada.
                     </Text>
                  </Flex>}
            </Stack>

            {/* GALERÍA MASONRY / CARPETA */}
            <Stack w="100%">
               <FileInput
                  label="Imágenes de ejemplo (Carpeta)"
                  placeholder='Seleccionar varias'
                  multiple
                  leftSection={<IconFiles size={20} />}
                  disabled={isLoading}
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  onChange={handleMultipleFiles}
               />

               <Flex gap={8} align='center'>
                  <IconChevronDown size={14} />
                  <Text size="xs" c="dimmed">
                     {form.values.images?.length || 0} archivos en la galería.
                  </Text>
               </Flex>

               {form.values?.images?.length > 0 && (
                  <Flex
                     gap={12}
                     w='100%'
                     p="xs"
                     style={{
                        overflowX: 'auto',
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        minHeight: '120px'
                     }}
                  >
                     {form.values.images.map((img, index) => (
                        <Box key={index} pos="relative" style={{ flexShrink: 0 }}>
                           <Image
                              radius="sm"
                              h={100}
                              w="auto"
                              fallbackSrc="https://placehold.co/100x100?text=PDF"
                              src={img}
                           />
                           <ActionIcon
                              variant="filled"
                              color="red"
                              radius="xl"
                              size="sm"
                              pos="absolute"
                              top={-8}
                              right={-8}
                              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                              onClick={() => handleRemoveImage(index)}
                           >
                              <IconX size={14} />
                           </ActionIcon>
                        </Box>
                     ))}
                  </Flex>
               )}
            </Stack>
         </Stack>

         <Stack

            gap={10} p={10} style={{ borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-text-primary)' }}>
            <Flex align='center' gap={6}>
               <IconDownload />
               <Text size='lg'>Archivo de descarga</Text>
            </Flex>
            <FileInput
               label={`Subir archivo ${form.values.type ? `(${form.values.type})` : ""}`}
               placeholder="Seleccionar archivo para descarga"
               leftSection={<IconFiles size={16} />}
               disabled={isLoading || !form.values.type || form.values?.type === 'enlace'}
               // Dinámicamente aceptamos según el tipo seleccionado
               accept={
                  form.values.type === 'imagen' ? 'image/*' :
                     form.values.type === 'video' ? 'video/*' :
                        form.values.type === 'documento' ? '.pdf' :
                           form.values.type === 'zip' ? '.zip,.rar' : '*'
               }
               onChange={handleDownloadFile}
            />
            {!form.values.type && <Text size="xs">X Primero selecciona el tipo de recurso</Text>}
            {form.values.download_url && isBase64(form.values.download_url) && (
               <Text size="xs">✓ Archivo listo para subir</Text>
            )}

         </Stack>

         <TextInput
            {...form.getInputProps("external_url")}
            label='URL Externa / Video (YouTube/Drive)'
            placeholder="https://..."
            leftSection={<IconVideo size={16} />}
            required={form?.values?.type === 'enlace'}
            disabled={isLoading}
         />
         <Stack gap={4}>
            <FileInput
               label='Subir Tutorial (Imagen o Video)'
               placeholder="Click para cargar archivo"
               leftSection={<IconVideo size={16} />}
               accept="image/*,video/*"
               disabled={isLoading}
               onChange={handleTutorialFile}
            />

            {/* Previsualización del Tutorial */}
            {form.values.tutorial_url && (
               <Box pos="relative" mt="xs" w="max-content">
                  {isVideoContent(form.values.tutorial_url) ? (
                     <video
                        src={form.values.tutorial_url}
                        style={{ height: 120, borderRadius: '8px', border: '1px solid #eee' }}
                        controls
                     />
                  ) : (
                     <Image
                        h={120}
                        radius="md"
                        src={form.values.tutorial_url}
                     />
                  )}
                  <ActionIcon
                     variant="filled"
                     color="red"
                     radius="xl"
                     size="xs"
                     pos="absolute"
                     top={-5}
                     right={-5}
                     onClick={() => form.setFieldValue("tutorial_url", "")}
                  >
                     <IconX size={12} />
                  </ActionIcon>
               </Box>
            )}
         </Stack>

         {!form.values?.is_active &&
            <Flex align='center' gap={10}
               my={20}
               bg='var(--color-badge-red)'
               maw='max-content'
               c='#fff'
               px={10}
               py={6}
               fz='var(--fs-small)'
               style={{
                  borderRadius: 'var(--border-radius-sm)'
               }}
            >
               <IconExclamationCircle />

               <strong>
                  Este recurso esta inactivo, quedará oculto para los usuarios
               </strong>
            </Flex>
         }

         <Button
            mt={30}
            w='max-content'
            type="submit"
            loading={isLoading}
         >
            {form.values._id ? 'Actualizar recurso' : 'Guardar recurso'}
         </Button>
      </Stack>
   );
}